import React, { Suspense, lazy } from 'react';

import Banner from '@/components/modular-grid/Banner/Banner';
import GameBannerGhost from '@/components/modular-grid/GameBanner/GameBannerGhost';
import GameDualGhost from '@/components/modular-grid/GameDual/GameDualGhost';
import GridGhost from '@/components/modular-grid/Grid/GridGhost';
import {
  Pattern,
  PatternGridGhost,
} from '@/components/modular-grid/PatternGrid';
import { TallSingleBlockGhost } from '@/components/modular-grid/TallSingleBlock';
import { TopGamesGhost } from '@/components/modular-grid/TopGames';
import { GameTileProps, OverlayProps } from '@/components/modular-grid/types';
import WidgetGridGhost from '@/components/modular-grid/WidgetGrid/WidgetGridGhost';
import { BannerSlideWithContent } from '@/types/category/bannerTypes';
import {
  AvailableGridTypes,
  CategoriesWithContentTypes,
  CategoryBannerWithContentType,
  CategoryFinancialWebAttributes,
  CategoryGameCollectionWithContentType,
  CategoryGameGridWithContentType,
  CategoryJackpotGridWithContentType,
  CategoryShuffleGameGridWithContentType,
  CategoryStaticTexts,
  NewSlidesTypes,
} from '@/types/category/categoryTypes';
import { JackpotSlotMapped } from '@/types/jackpotTypes';
import { GridInfoType } from '@/types/pageTypes';
import { ShuffleWidgetMapped } from '@/types/shuffleWidgetTypes';
import { StrikeGoldSlotMapped } from '@/types/strikeGoldTypes';

const GameBanner = lazy(() =>
  import(
    /* webpackChunkName: "GameBanner" */

    '../modular-grid/GameBanner/GameBanner'
  )
);
const GameDual = lazy(() =>
  import(
    /* webpackChunkName: "GameDual" */

    '../modular-grid/GameDual'
  )
);
const GameList = lazy(() =>
  import(
    /* webpackChunkName: "GameList" */

    '../modular-grid/GameList'
  )
);
const Grid = lazy(() =>
  import(
    /* webpackChunkName: "Grid" */

    '../modular-grid/Grid'
  )
);
const ExtendedGrid = lazy(() =>
  import(
    /* webpackChunkName: "ExtendedGrid" */

    '../modular-grid/ExtendedGrid'
  )
);
const WidgetGrid = lazy(() =>
  import(
    /* webpackChunkName: "WidgetGrid" */

    '../modular-grid/WidgetGrid/WidgetGrid'
  )
);
const TallSingleBlock = lazy(() =>
  import(
    /* webpackChunkName: "TallSingleBlock" */

    '../modular-grid/TallSingleBlock'
  )
);
const TopGames = lazy(() =>
  import(
    /* webpackChunkName: "TopGames" */

    '../modular-grid/TopGames'
  )
);
const PatternGrid = lazy(() =>
  import(
    /* webpackChunkName: "PatternGrid" */

    '../modular-grid/PatternGrid'
  )
);

const GAME_TILES_BREAKPOINT: { [key: string]: number } = {
  SMALL: 698,
  MEDIUM: 924,
  LARGE: 1150,
  XLARGE: 1250,
};

const GAMES_AMOUNT_FOR_BREAKPOINT: { [key: string]: number } = {
  SMALL: 3,
  MEDIUM: 4,
  LARGE: 5,
  XLARGE: 10,
};

export const getNumberOfTilesInRow = (windowWidth: number) => {
  for (const [key, size] of Object.entries(GAME_TILES_BREAKPOINT)) {
    if (windowWidth < size) {
      return GAMES_AMOUNT_FOR_BREAKPOINT[key];
    }
  }
  // fallback for largest size
  return GAMES_AMOUNT_FOR_BREAKPOINT.XLARGE;
};

const gridTypeMap = (type: string) => {
  switch (type) {
    case 'Jackpot Nine':
      return 'jackpotNine';
    case 'Collection':
      return 'collection';
    case 'Dynamic Grid':
      return 'collection';
    case 'Grid':
    default:
      return 'grid';
  }
};

export const createComponent = ({
  type,
  gameList,
  widgetSlot,
  rest,
  percentageWidth,
  overlay,
  banner,
  hidden,
  locale,
  currency,
  index,
  categoryAttributes,
}: {
  type: AvailableGridTypes;
  gameList?: GameTileProps[];
  widgetSlot?: JackpotSlotMapped | StrikeGoldSlotMapped | ShuffleWidgetMapped;
  rest: GridInfoType;
  percentageWidth?: number;
  overlay?: OverlayProps;
  banner?: BannerSlideWithContent['banner'];
  hidden?: boolean;
  locale: string;
  currency?: string;
  index?: number;
  categoryAttributes?: CategoryFinancialWebAttributes;
}) => {
  switch (type) {
    case 'Banner':
      return (
        <Banner
          key={rest.key}
          index={index}
          banner={banner}
          slidesAmount={rest.slidesAmount}
          onClick={rest.onClick}
          onInfoClick={rest.onInfoClick}
          onRouteClick={rest.onRouteClick}
          onLinkClick={rest.onLinkClick}
          gameDetailsInfo={rest.gameDetailsInfo}
          gameInfoTooltip={rest.gameInfoTooltip}
          hidden={hidden}
          categoryAttributes={categoryAttributes}
        />
      );
    case 'Game Dual':
      return (
        <Suspense key={rest.key} fallback={<GameDualGhost />}>
          <GameDual
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            overlay={overlay}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            gamesLimit={rest.gamesLimit}
            initialIndex={rest.slideInitialIndex}
            size={rest.size}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    case 'Game List':
      return (
        <GameList
          gamesLimit={rest.gamesLimit}
          gameList={gameList}
          onClick={rest.onClick}
          onInfoClick={rest.onInfoClick}
          overlay={overlay}
          initialIndex={rest.slideInitialIndex}
          size={rest.size}
          locale={locale}
          currency={currency}
          categoryAttributes={categoryAttributes}
          gameInfoTooltip={rest.gameInfoTooltip}
        />
      );
    case 'Game Banner':
      return (
        <Suspense key={rest.key} fallback={<GameBannerGhost />}>
          <GameBanner
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            overlay={overlay}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            gamesLimit={rest.gamesLimit}
            initialIndex={rest.slideInitialIndex}
            size={rest.size}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    case 'Jackpot Banner':
    case 'Jackpot Hero':
    case 'Game Shuffle Hero':
    case 'Widget Banner':
    case 'Widget Hero': {
      return (
        <Suspense key={rest.key} fallback={<WidgetGridGhost />}>
          <WidgetGrid
            gameList={gameList}
            widgetTile={widgetSlot}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            onShuffleClick={rest.onShuffleClick}
            overlay={overlay}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            type={
              type === 'Widget Banner' || type === 'Jackpot Banner'
                ? 'Banner'
                : 'Hero'
            }
            gamesLimit={rest.gamesLimit}
            initialIndex={rest.slideInitialIndex}
            size={rest.size}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Extended Single Block': {
      return (
        <Suspense key={rest.key} fallback={<GridGhost extended={true} />}>
          <ExtendedGrid
            percentageWidth={percentageWidth}
            gameList={gameList}
            gamesLimit={rest.gamesLimit}
            type={gridTypeMap(type)}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            overlay={overlay}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            initialIndex={rest.slideInitialIndex}
            size={rest.size}
            categoryAttributes={categoryAttributes}
            staticTexts={rest.staticTexts}
          />
        </Suspense>
      );
    }
    case 'Tall Single Block': {
      return (
        <Suspense key={rest.key} fallback={<TallSingleBlockGhost />}>
          <TallSingleBlock
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Top Games': {
      return (
        <Suspense key={rest.key} fallback={<TopGamesGhost />}>
          <TopGames
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Vertical Horizontal': {
      const pattern: Pattern = [
        'tall-vertical',
        'small',
        'small',
        'small',
        'small',
        'wide-horizontal',
      ];
      return (
        <Suspense
          key={rest.key}
          fallback={<PatternGridGhost pattern={pattern} />}
        >
          <PatternGrid
            pattern={pattern}
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Dual Vertical Horizontal': {
      const pattern: Pattern = [
        'tall-vertical',
        'tall-vertical',
        'small',
        'small',
        'small',
        'small',
        'wide-horizontal',
      ];
      return (
        <Suspense
          key={rest.key}
          fallback={<PatternGridGhost pattern={pattern} />}
        >
          <PatternGrid
            pattern={pattern}
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Horizontal Vertical': {
      const pattern: Pattern = [
        'wide-horizontal',
        'small',
        'small',
        'small',
        'small',
        'tall-vertical',
      ];
      return (
        <Suspense
          key={rest.key}
          fallback={<PatternGridGhost pattern={pattern} />}
        >
          <PatternGrid
            pattern={pattern}
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    case 'Dual Horizontal Vertical': {
      const pattern: Pattern = [
        'wide-horizontal',
        'wide-horizontal',
        'small',
        'small',
        'small',
        'small',
        'tall-vertical',
      ];
      return (
        <Suspense
          key={rest.key}
          fallback={<PatternGridGhost pattern={pattern} />}
        >
          <PatternGrid
            pattern={pattern}
            gameList={gameList}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
    }
    default:
      return (
        <Suspense key={rest.key} fallback={<GridGhost />}>
          <Grid
            percentageWidth={percentageWidth}
            gameList={gameList}
            gamesLimit={rest.gamesLimit}
            type={gridTypeMap(type)}
            onClick={rest.onClick}
            onInfoClick={rest.onInfoClick}
            overlay={overlay}
            gameDetailsInfo={rest.gameDetailsInfo}
            gameInfoTooltip={rest.gameInfoTooltip}
            initialIndex={rest.slideInitialIndex}
            size={rest.size}
            categoryAttributes={categoryAttributes}
          />
        </Suspense>
      );
  }
};

const createSlides = (
  category:
    | CategoryGameGridWithContentType
    | CategoryJackpotGridWithContentType
    | CategoryGameCollectionWithContentType
    | CategoryShuffleGameGridWithContentType,
  getOnClickHandler: (index: number) => (args: object) => void,
  getOnInfoClickHandler: (index: number) => (args: object) => void,
  getOnRouteClickHandler: (index: number) => (args: object) => void,
  getOnShuffleClickHandler: (index: number) => (args: object) => void,
  percentageWidth: number,
  locale: string,
  gameInfoTooltip: string,
  currency?: string,
  overlay?: OverlayProps,
  singleSlide?: boolean,
  size?: 'small' | 'medium',
  pageTitle?: string,
  staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>
) => {
  const { slides, gameDetailsInfo } = category;
  if (!slides) return null;

  return slides
    .slice(0, singleSlide ? 1 : slides.length)
    .map((slide, index) => {
      const last = index === slides.length - 1 && index !== 0;
      const slidePercentageWidth = last ? percentageWidth : undefined;
      const {
        type,
        gameList,
        widgetSlot,
        gamesLimit,
        slideInitialIndex,
      } = slide;
      return createComponent({
        type,
        gameList,
        widgetSlot,
        rest: {
          gamesLimit,
          key: `${slide.type}-${index}`,
          onClick: getOnClickHandler(index),
          onInfoClick: getOnInfoClickHandler(index),
          onRouteClick: getOnRouteClickHandler(index),
          onShuffleClick: getOnShuffleClickHandler(index),
          gameDetailsInfo: !!gameDetailsInfo,
          slideInitialIndex,
          size,
          gameInfoTooltip,
          staticTexts,
        },
        percentageWidth: slidePercentageWidth,
        overlay,
        locale,
        currency,
        categoryAttributes: {
          title: category.title,
          topCategory: pageTitle,
        },
      });
    });
};

const createBannerSlides = (
  category: CategoryBannerWithContentType,
  getOnClickHandler: (index: number) => (args: object) => void,
  getOnInfoClickHandler: (index: number) => (args: object) => void,
  getOnRouteClickHandler: (index: number) => (args: object) => void,
  getOnLinkClickHandler: (index: number) => (args: object) => void,
  locale: string,
  gameInfoTooltip: string,
  singleSlide?: boolean,
  currency?: string,
  pageTitle?: string
) => {
  const { slides, type, gameDetailsInfo } = category;
  if (!slides) return null;

  return slides.map((slide: NewSlidesTypes, index: number) =>
    createComponent({
      type,
      hidden:
        slide.banner && slide.banner.personalization
          ? slide.banner.personalization.rendered === false
          : false,
      banner: slide.banner,
      index: index,
      rest: {
        key: `${slide.type}-${index}`,
        gameDetailsInfo: !!gameDetailsInfo,
        slidesAmount: slides.length,
        onClick: getOnClickHandler(index),
        onInfoClick: getOnInfoClickHandler(index),
        onRouteClick: getOnRouteClickHandler(index),
        onLinkClick: getOnLinkClickHandler(index),
        gameInfoTooltip,
      },
      locale,
      currency,
      categoryAttributes: {
        title: category.title,
        topCategory: pageTitle,
      },
    })
  );
};

export const createSlideComponent = ({
  category,
  percentageWidth,
  onGameTileClick,
  onGameTileInfoClick,
  onRouteTileClick,
  onLinkTileClick,
  onShuffleClick,
  overlay,
  singleSlide,
  size,
  locale,
  currency,
  pageTitle,
  gameInfoTooltip,
  staticTexts,
}: {
  category: CategoriesWithContentTypes;
  windowWidth: number;
  percentageWidth: number;
  onGameTileClick?: (args: object) => void;
  onGameTileInfoClick?: (args: object) => void;
  onRouteTileClick?: (args: object) => void;
  onLinkTileClick?: (args: object) => void;
  onShuffleClick?: (args: object) => void;
  overlay?: OverlayProps;
  singleSlide?: boolean;
  size?: 'small' | 'medium';
  locale: string;
  currency?: string;
  pageTitle?: string;
  gameInfoTooltip: string;
  staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>;
}): React.ReactNode[] | null => {
  const { title, id } = category;
  const getOnClickHandler = (fn: ((args: object) => void) | undefined) => (
    idx: number
  ) => (args: object) => {
    fn &&
      fn({
        sectionTitle: title,
        id,
        slideIdx: idx,
        ...args,
      });
  };

  switch (category.component_type) {
    case 'banner':
      return createBannerSlides(
        category,
        getOnClickHandler(onGameTileClick),
        getOnClickHandler(onGameTileInfoClick),
        getOnClickHandler(onRouteTileClick),
        getOnClickHandler(onLinkTileClick),
        locale,
        gameInfoTooltip,
        singleSlide,
        currency,
        pageTitle
      );
    default:
      return createSlides(
        category,
        getOnClickHandler(onGameTileClick),
        getOnClickHandler(onGameTileInfoClick),
        getOnClickHandler(onRouteTileClick),
        getOnClickHandler(onShuffleClick),
        percentageWidth,
        locale,
        gameInfoTooltip,
        currency,
        overlay,
        singleSlide,
        size,
        pageTitle,
        staticTexts
      );
  }
};
